import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { CloseOutlined, SaveOutlined, SearchOutlined } from "@mui/icons-material";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";

import { useUpdateRolesMutation } from "state/rtkQuery/access/accessMutations";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import {
  buildPermissionTree,
  getWildcardImpliedPermissions,
  groupPermissionsByNamespace,
} from "utils/accessManagement/roleDisplay";
import { showToast } from "utils/reusableToast";

import "./RoleEditModal.css";

// A role is treated as "high-privilege" for the removal-confirmation rule when
// its priority is in the top tier of all loaded roles, or it already carries a
// large permission set — either signal suggests broad platform impact. LOWER
// priority number means MORE senior (e.g. EnterpriseAdmin=10 outranks
// User=90), confirmed against real GET /roles data — so the most senior role
// is the one with the LOWEST number, found via Math.min.
const isHighPrivilegeRole = (role, allRoles) => {
  if (!role) return false;
  const priorities = allRoles.map((r) => r.priority ?? Number.MAX_SAFE_INTEGER);
  const minPriority = priorities.length ? Math.min(...priorities) : Number.MAX_SAFE_INTEGER;
  const isTopPriority = minPriority < Number.MAX_SAFE_INTEGER && (role.priority ?? Number.MAX_SAFE_INTEGER) <= minPriority;
  const hasManyPermissions = (role.permissions || []).length >= 20;
  return isTopPriority || hasManyPermissions;
};

const RoleEditDrawer = ({ role, knownPermissionCatalog, allRoles, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState("");
  const [activeNamespace, setActiveNamespace] = useState(null);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const [updateRoles, { isLoading, isError, error, reset }] = useUpdateRolesMutation();

  useEffect(() => {
    if (role) {
      setForm({
        roleName: role.role_name || "",
        description: role.description || "",
        priority: role.priority ?? 0,
        isActive: role.is_active !== false,
        permissions: new Set(role.permissions || []),
      });
      setSearch("");
      setActiveNamespace(null);
      setConfirmRemoval(false);
      reset();
    }
  }, [role]);

  const catalog = useMemo(() => {
    const set = new Set([...knownPermissionCatalog, ...(role?.permissions || [])]);
    return Array.from(set).sort();
  }, [knownPermissionCatalog, role]);

  const groups = useMemo(() => groupPermissionsByNamespace(catalog), [catalog]);

  // Permissions implied by a checked wildcard (e.g. "platform:*" implies every
  // other "platform:..." permission) are force-checked and locked here so the
  // admin can see exactly what the wildcard grants without editing it directly.
  const impliedByGroup = useMemo(() => {
    const map = new Map();
    groups.forEach((group) => {
      map.set(
        group.namespace,
        getWildcardImpliedPermissions(form?.permissions ?? new Set(), group.permissions)
      );
    });
    return map;
  }, [groups, form]);

  // The permission set actually saved: explicit selections plus anything a
  // checked wildcard implies, so the role's real grant matches what's visibly checked.
  const effectivePermissions = useMemo(() => {
    if (!form) return new Set();
    const all = new Set(form.permissions);
    impliedByGroup.forEach((implied) => implied.forEach((p) => all.add(p)));
    return all;
  }, [form, impliedByGroup]);

  // The tab list always shows every namespace; only the active group's own
  // permissions are search-filtered, since the search box lives inside that
  // group's panel rather than filtering across all groups at once.
  const resolvedActiveNamespace =
    groups.find((g) => g.namespace === activeNamespace)?.namespace ?? groups[0]?.namespace ?? null;
  const activeGroupUnfiltered = groups.find((g) => g.namespace === resolvedActiveNamespace) ?? null;
  const activeGroup = useMemo(() => {
    if (!activeGroupUnfiltered) return null;
    if (!search.trim()) return activeGroupUnfiltered;
    const q = search.toLowerCase();
    return {
      ...activeGroupUnfiltered,
      permissions: activeGroupUnfiltered.permissions.filter((p) => p.toLowerCase().includes(q)),
    };
  }, [activeGroupUnfiltered, search]);

  // GitHub-scopes-style tree: wildcards nest recursively (a more specific
  // wildcard like "auth:role-assignment:*" nests under a broader one like
  // "auth:*"), with plain permissions as leaves under whichever scope claims them.
  const activeGroupTree = useMemo(
    () => (activeGroup ? buildPermissionTree(activeGroup.permissions) : { parents: [], leaves: [] }),
    [activeGroup]
  );

  if (!role || !form) return null;

  const isImplied = (permission, namespace) => impliedByGroup.get(namespace)?.has(permission) ?? false;

  const originalPermissions = new Set(role.permissions || []);
  const added = [...effectivePermissions].filter((p) => !originalPermissions.has(p));
  const removed = [...originalPermissions].filter((p) => !effectivePermissions.has(p));
  const hasChanges =
    added.length > 0 ||
    removed.length > 0 ||
    form.roleName !== role.role_name ||
    form.description !== (role.description || "") ||
    form.priority !== (role.priority ?? 0) ||
    form.isActive !== (role.is_active !== false);

  const togglePermission = (permission, namespace) => {
    if (isImplied(permission, namespace)) return; // locked while a wildcard covers it
    setForm((f) => {
      const next = new Set(f.permissions);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return { ...f, permissions: next };
    });
  };

  const toggleGroup = (group, checkAll) => {
    setForm((f) => {
      const next = new Set(f.permissions);
      group.permissions.forEach((p) => (checkAll ? next.add(p) : next.delete(p)));
      return { ...f, permissions: next };
    });
  };

  const submit = async () => {
    try {
      const result = await updateRoles([
        {
          id: role.id,
          role_name: form.roleName.trim(),
          description: form.description.trim() || undefined,
          priority: Number(form.priority),
          permissions: Array.from(effectivePermissions),
          is_active: form.isActive,
        },
      ]).unwrap();

      if (result.not_updated?.length > 0) {
        showToast({
          title: "Role update failed",
          description: result.not_updated[0]?.message || "The role could not be updated.",
          type: "error",
        });
        return;
      }

      showToast({ title: "Role updated", description: `"${form.roleName}" was saved successfully.`, type: "success" });
      onSaved();
    } catch {
      // isError/error render inline; form state stays intact for correction.
    }
  };

  const handleSaveClick = () => {
    if (removed.length > 0 && isHighPrivilegeRole(role, allRoles)) {
      setConfirmRemoval(true);
      return;
    }
    submit();
  };

  const activeGroupAllSelected =
    !!activeGroup && activeGroup.permissions.every((p) => effectivePermissions.has(p));
  const hasParents = activeGroupTree.parents.length > 0;

  const renderCheckbox = (permission, namespace, { isParent = false } = {}) => {
    const implied = isImplied(permission, namespace);
    const checked = effectivePermissions.has(permission);
    return (
      <Tooltip
        key={permission}
        title={implied ? "Included because a parent scope above is selected" : ""}
        disableHoverListener={!implied}
      >
        <FormControlLabel
          className={`role-modal__checkbox-row${isParent ? " role-modal__checkbox-row--parent" : " role-modal__checkbox-row--child"}`}
          control={
            <Checkbox
              size="small"
              checked={checked}
              disabled={implied}
              onChange={() => togglePermission(permission, namespace)}
            />
          }
          label={isParent ? `${permission} — full access` : permission}
          sx={implied ? { opacity: 0.6 } : undefined}
        />
      </Tooltip>
    );
  };

  // Recursively renders a wildcard node and its nested children/leaves.
  const renderTreeNode = (node, namespace, depth = 0) => (
    <div key={node.permission} className="role-modal__scope-tree" style={{ marginLeft: depth > 0 ? 26 : 0 }}>
      {renderCheckbox(node.permission, namespace, { isParent: true })}
      <div className="role-modal__scope-children">
        {node.children.map((child) => renderTreeNode(child, namespace, depth + 1))}
        {node.leaves.map((permission) => renderCheckbox(permission, namespace))}
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        open={!!role}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { width: "min(1200px, 94vw)", height: "min(820px, 90vh)" } }}
      >
        <div className="role-modal">
          {/* ── Header ── */}
          <div className="role-modal__header">
            <div>
              <p className="role-modal__eyebrow">Access Management</p>
              <h2 className="role-modal__title">Edit role</h2>
              <code className="role-modal__id">{role.id}</code>
            </div>
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose} sx={{ color: "#9ca3af" }}>
                <CloseOutlined />
              </IconButton>
            </Tooltip>
          </div>

          {/* ── Body: basic fields left, permission matrix right ── */}
          <div className="role-modal__body">
            <div className="role-modal__fields">
              <TextField
                label="Role name"
                required
                size="small"
                fullWidth
                value={form.roleName}
                onChange={(e) => setForm((f) => ({ ...f, roleName: e.target.value }))}
              />
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              <TextField
                label="Priority"
                type="number"
                size="small"
                fullWidth
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              />
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} size="small" />}
                label={form.isActive ? "Active" : "Inactive"}
              />

              <Divider sx={{ my: 1 }} />

              <div className="role-modal__summary">
                <span className="role-modal__summary-count">{effectivePermissions.size}</span>
                <span className="role-modal__summary-label">permissions selected</span>
              </div>

              {(added.length > 0 || removed.length > 0) && (
                <Alert severity="info" sx={{ fontSize: "12px" }}>
                  {added.length > 0 && <div>+ Adding {added.length} permission{added.length === 1 ? "" : "s"}</div>}
                  {removed.length > 0 && <div>− Removing {removed.length} permission{removed.length === 1 ? "" : "s"}</div>}
                </Alert>
              )}

              {isError && (
                <Alert severity="error" sx={{ fontSize: "12px" }}>
                  {extractApiErrorMessage(error, "Failed to update role. Please try again.")}
                </Alert>
              )}
            </div>

            <div className="role-modal__permissions">
              {groups.length === 0 ? (
                <p style={{ fontSize: 13, color: "#9ca3af" }}>This role has no permissions.</p>
              ) : (
                <div className="role-modal__tabbed">
                  {/* ── Left: namespace tab list (always shows every namespace) ── */}
                  <div className="role-modal__tabs">
                    <div className="role-modal__tabs-header">
                      <span className="role-modal__tabs-title">Service type</span>
                    </div>
                    {groups.map((group) => {
                      const selectedInGroup = group.permissions.filter((p) => effectivePermissions.has(p)).length;
                      const isActive = resolvedActiveNamespace === group.namespace;
                      return (
                        <button
                          key={group.namespace}
                          type="button"
                          className={`role-modal__tab${isActive ? " active" : ""}`}
                          onClick={() => {
                            setActiveNamespace(group.namespace);
                            setSearch("");
                          }}
                        >
                          <span className="role-modal__tab-label">{group.label}</span>
                          <span className="role-modal__tab-count">{selectedInGroup}/{group.permissions.length}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* ── Right: full-height scope tree for the active namespace only ── */}
                  {activeGroupUnfiltered && (
                    <div className="role-modal__scope-panel">
                      <div className="role-modal__scope-panel-header">
                        <span className="role-modal__scope-panel-title">{activeGroupUnfiltered.label} permissions</span>
                        <label className="role-modal__scope-search">
                          <SearchOutlined sx={{ fontSize: 16, color: "#9ca3af", flexShrink: 0 }} />
                          <input
                            type="text"
                            placeholder={`Search permissions inside ${activeGroupUnfiltered.label}…`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label={`Search permissions inside ${activeGroupUnfiltered.label}`}
                          />
                        </label>
                        {/* Redundant when a wildcard parent covers the group — checking
                            it already selects everything, so "Select all" only earns
                            its place in groups with no single "grants everything" scope. */}
                        {!hasParents && (
                          <button
                            type="button"
                            className="role-modal__select-all"
                            onClick={() => toggleGroup(activeGroup, !activeGroupAllSelected)}
                          >
                            {activeGroupAllSelected ? "Clear all" : "Select all"}
                          </button>
                        )}
                      </div>
                      <div className="role-modal__scope-panel-body">
                        {activeGroup.permissions.length === 0 ? (
                          <p style={{ fontSize: 13, color: "#9ca3af" }}>No permissions match &quot;{search}&quot;.</p>
                        ) : (
                          <>
                            {activeGroupTree.parents.map((node) => renderTreeNode(node, activeGroup.namespace))}
                            {activeGroupTree.leaves.map((permission) => renderCheckbox(permission, activeGroup.namespace))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="role-modal__footer">
            <button type="button" className="role-modal__btn role-modal__btn--cancel" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button
              type="button"
              className="role-modal__btn role-modal__btn--save"
              onClick={handleSaveClick}
              disabled={isLoading || !hasChanges}
            >
              {isLoading ? "Saving…" : (<><SaveOutlined style={{ fontSize: 16, marginRight: 6 }} />Save changes</>)}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog open={confirmRemoval} onClose={() => setConfirmRemoval(false)}>
        <DialogTitle>Remove permissions from a high-privilege role?</DialogTitle>
        <DialogContent sx={{ maxWidth: 420 }}>
          <DialogContentText component="div">
            <p>
              <strong>{form.roleName}</strong> is a high-priority role. Removing the following permissions
              will immediately reduce access for every identity assigned this role:
            </p>
            <ul>
              {removed.map((p) => (
                <li key={p} style={{ fontFamily: "monospace", fontSize: 12 }}>{p}</li>
              ))}
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoval(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setConfirmRemoval(false);
              submit();
            }}
          >
            Remove permissions
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

RoleEditDrawer.propTypes = {
  role: PropTypes.object,
  knownPermissionCatalog: PropTypes.arrayOf(PropTypes.string).isRequired,
  allRoles: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default RoleEditDrawer;
